/**
 * @docs: docs/core/architecture/SCOUT_GATEWAY_REMOTE.md
 *
 * The owner's People on an SG: who asked to join (by scanning the QR code on
 * the SG screen), and who has access. Crew look only; a fleet operator has the
 * owner's rights on the SG. A charter crew gets an end date.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type GatewayJoinRequest } from '../../services/api';
import { useToastStore } from '../../store/toastStore';

const ROLE: Record<string, string> = { crew: 'Crew · looks only', fleet: 'Fleet operator · can change settings' };

function who(p: { name: string | null; email: string | null; user_id: string }) {
  return p.name || p.email || p.user_id;
}

function Approve({ deviceId, request }: { deviceId: string; request: GatewayJoinRequest }) {
  const qc = useQueryClient();
  const [role, setRole] = useState<'crew' | 'fleet'>('crew');
  const [until, setUntil] = useState('');
  const toast = useToastStore.getState().addToast;
  const done = () => qc.invalidateQueries({ queryKey: ['gateway-people', deviceId] });
  const approve = useMutation({
    mutationFn: () => api.approveGatewayPerson(deviceId, { user_id: request.user_id, role, until: until ? new Date(`${until}T23:59:59`).toISOString() : null }),
    onSuccess: () => { done(); toast({ type: 'success', title: 'Approved', message: `${who(request)} can open this SG.` }); },
    onError: () => toast({ type: 'error', title: 'Not approved', message: 'Check the end date and try again.' }),
  });
  const decline = useMutation({
    mutationFn: () => api.removeGatewayPerson(deviceId, request.user_id),
    onSuccess: done,
  });
  return (
    <li className="rounded-lg border border-slate-600 p-3" data-testid={`request-${request.user_id}`}>
      <p className="text-white">{who(request)}</p>
      <p className="text-sm text-slate-400">Scanned the SG’s QR code on board and asks to join</p>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <select aria-label="Role" value={role} onChange={(e) => setRole(e.target.value as 'crew' | 'fleet')}
          className="bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-sm">
          <option value="crew">Crew (looks only)</option>
          <option value="fleet">Fleet operator</option>
        </select>
        <label className="text-sm text-slate-300" htmlFor={`until-${request.user_id}`}>Until</label>
        <input id={`until-${request.user_id}`} type="date" value={until} onChange={(e) => setUntil(e.target.value)}
          className="bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-sm" />
        <button onClick={() => approve.mutate()} disabled={approve.isPending}
          className="px-3 py-1 rounded bg-cyan-600 hover:bg-cyan-500 text-white text-sm">Approve</button>
        <button onClick={() => decline.mutate()} className="px-3 py-1 rounded bg-slate-700 hover:bg-slate-600 text-white text-sm">Decline</button>
      </div>
    </li>
  );
}

export function SgPeopleCard({ deviceId }: { deviceId: string }) {
  const qc = useQueryClient();
  const { data, isError } = useQuery({ queryKey: ['gateway-people', deviceId], queryFn: () => api.getGatewayPeople(deviceId), retry: false });
  const remove = useMutation({
    mutationFn: (userId: string) => api.removeGatewayPerson(deviceId, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gateway-people', deviceId] }),
  });
  if (isError) return null;

  return (
    <div className="bg-slate-700/50 rounded-lg p-3" data-testid="sg-people">
      <p className="text-sm text-slate-400">People</p>
      <p className="text-sm text-slate-300 mt-0.5">
        Others join by scanning the QR code in SG’s Tools › Remote access, on board. You approve them here.
      </p>
      {data && data.requests.length > 0 && (
        <ul className="space-y-2 mt-3">
          {data.requests.map((r) => <Approve key={r.user_id} deviceId={deviceId} request={r} />)}
        </ul>
      )}
      {data && data.members.length > 0 && (
        <ul className="divide-y divide-slate-600/60 mt-3">
          {data.members.map((m) => (
            <li key={m.user_id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <div className="min-w-0">
                <p className="text-white">{who(m)}</p>
                <p className={`text-sm ${m.expired ? 'text-amber-300' : 'text-slate-400'}`}>
                  {ROLE[m.role]}
                  {m.until ? ` · ${m.expired ? 'ended' : 'until'} ${new Date(m.until).toLocaleDateString('en-NZ')}` : ''}
                </p>
              </div>
              <button onClick={() => remove.mutate(m.user_id)} className="px-3 py-1 rounded bg-slate-700 hover:bg-red-700 text-white text-sm">
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {data && data.members.length === 0 && data.requests.length === 0 && (
        <p className="text-sm text-slate-400 mt-2">Only you, for now.</p>
      )}
    </div>
  );
}
